import graphql__869ff77fd066ccf7e1a2 from "./__generated__/AnalysisServerCostViewQuery.graphql.ts";import * as React from "react";
import {useMemo, useState} from "react";
import {useOutletContext} from "react-router";
import {usePreloadedQuery} from "react-relay/hooks";
import {graphql} from "react-relay";
import {useIntl} from "react-intl";
import {formatAmount} from "../helper/revenueFormat";
import Panel from "./presentational/Panel";
import KpiCard from "./presentational/KpiCard";
import ActionButton from "./presentational/button/ActionButton";
import {faRotateLeft} from "@fortawesome/pro-regular-svg-icons";
import type {AnalysisServerCostViewQuery as AnalysisServerCostViewQueryType} from "./__generated__/AnalysisServerCostViewQuery.graphql";

const serverCostQuery = graphql__869ff77fd066ccf7e1a2;

type NodeType = {
    readonly platform: string
    readonly role: string | null
    readonly serverType: string
    readonly nodeCount: number
    readonly totalAmount: number
    readonly perNodeAmount: number
};

type AnalysisOutletContext = {
    serverCostQuery: any
};

const PLATFORM_LABELS: Record<string, string> = {
    HETZNER_CLOUD: 'Hetzner Cloud',
    HETZNER_DEDICATED: 'Hetzner Dedicated',
};

const AnalysisServerCostView: React.FC = () => {
    const intl = useIntl();
    const {serverCostQuery: queryRef} = useOutletContext<AnalysisOutletContext>();
    const data = usePreloadedQuery<AnalysisServerCostViewQueryType>(serverCostQuery, queryRef);
    const report = data.hetznerNodeCosts;
    const types = report.types as ReadonlyArray<NodeType>;

    // Per server type, the percentage cost change to apply (e.g. the Hetzner price increase of that
    // type, or a server-type switch expressed as a cost ratio). Keyed by platform|role|serverType.
    const [changes, setChanges] = useState<Record<string, string>>({});

    const keyOf = (t: NodeType) => `${t.platform}|${t.role ?? ''}|${t.serverType}`;
    const factorOf = (t: NodeType): number => {
        const raw = changes[keyOf(t)];
        if (raw === undefined || raw.trim() === '') {
            return 1;
        }
        const pct = Number(raw.replace(',', '.'));
        return Number.isNaN(pct) ? 1 : 1 + pct / 100;
    };

    const {simulatedServerCost, changeCount} = useMemo(() => {
        let total = 0;
        let count = 0;
        for (const t of types) {
            const factor = factorOf(t);
            total += Math.round(t.totalAmount * factor);
            if (factor !== 1) {
                count++;
            }
        }
        return {simulatedServerCost: total, changeCount: count};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [types, changes]);

    // The full cluster cost reconciles with the invoice; the part beyond the bare server nodes
    // (volumes, load balancers, traffic, IPs, RAM upgrades) is held constant in the simulation.
    const currentTotal = report.totalClusterCost;
    const overhead = report.totalClusterCost - report.totalServerCost;
    const simulatedTotal = simulatedServerCost + overhead;
    const deltaCost = simulatedTotal - currentTotal;

    const monthLabel = report.cutOff
        ? intl.formatDate(new Date(report.cutOff), {year: 'numeric', month: 'long', timeZone: 'UTC'})
        : '—';

    const platformLabel = (platform: string) => PLATFORM_LABELS[platform] ?? platform;
    const roleLabel = (role: string | null) =>
        role === 'cp' ? intl.formatMessage({id: 'view.analysisServerCost.role.cp'})
            : role === 'wk' ? intl.formatMessage({id: 'view.analysisServerCost.role.wk'})
                : '—';

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-medium text-gray-900">{intl.formatMessage({id: 'view.analysisServerCost.basis'}, {month: monthLabel})}</h2>
                <ActionButton onClick={() => setChanges({})} icon={faRotateLeft} disabled={changeCount === 0} label={intl.formatMessage({id: 'view.analysisServerCost.reset'})}/>
            </div>

            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisServerCost.intro'})}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label={intl.formatMessage({id: 'view.analysisServerCost.kpi.current'})} value={formatAmount(currentTotal)} sub={monthLabel}/>
                <KpiCard label={intl.formatMessage({id: 'view.analysisServerCost.kpi.simulated'})} value={formatAmount(simulatedTotal)}/>
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisServerCost.kpi.deltaCost'})}
                    value={`${deltaCost > 0 ? '+' : ''}${formatAmount(deltaCost)}`}
                    trend={deltaCost > 0 ? 'down' : deltaCost < 0 ? 'up' : 'neutral'}
                />
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisServerCost.kpi.deltaMargin'})}
                    value={`${-deltaCost > 0 ? '+' : ''}${formatAmount(-deltaCost)}`}
                    trend={-deltaCost > 0 ? 'up' : -deltaCost < 0 ? 'down' : 'neutral'}
                />
            </div>

            <Panel headline={intl.formatMessage({id: 'view.analysisServerCost.byType'})}>
                {types.length === 0 ? (
                    <p className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisServerCost.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    <th className="text-left pb-2 pr-3">{intl.formatMessage({id: 'view.analysisServerCost.col.type'})}</th>
                                    <th className="text-right pb-2 px-3">{intl.formatMessage({id: 'view.analysisServerCost.col.nodes'})}</th>
                                    <th className="text-right pb-2 px-3">{intl.formatMessage({id: 'view.analysisServerCost.col.perNode'})}</th>
                                    <th className="text-right pb-2 px-3">{intl.formatMessage({id: 'view.analysisServerCost.col.total'})}</th>
                                    <th className="text-right pb-2 px-3">{intl.formatMessage({id: 'view.analysisServerCost.col.change'})}</th>
                                    <th className="text-right pb-2 pl-3">{intl.formatMessage({id: 'view.analysisServerCost.col.newTotal'})}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {types.map(t => {
                                    const factor = factorOf(t);
                                    const newTotal = Math.round(t.totalAmount * factor);
                                    return (
                                        <tr key={keyOf(t)} className="border-t border-gray-100">
                                            <td className="py-1.5 pr-3 text-gray-900">
                                                {t.serverType}
                                                <span className="text-gray-400"> · {platformLabel(t.platform)} · {roleLabel(t.role)}</span>
                                            </td>
                                            <td className="py-1.5 px-3 text-right text-gray-500">{t.nodeCount}</td>
                                            <td className="py-1.5 px-3 text-right text-gray-400">{formatAmount(t.perNodeAmount)}</td>
                                            <td className="py-1.5 px-3 text-right text-gray-500">{formatAmount(t.totalAmount)}</td>
                                            <td className="py-1.5 px-3 text-right">
                                                <span className="inline-flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        value={changes[keyOf(t)] ?? ''}
                                                        placeholder="0"
                                                        onChange={event => setChanges(previous => ({...previous, [keyOf(t)]: event.target.value}))}
                                                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                                                    />
                                                    <span className="text-gray-400">%</span>
                                                </span>
                                            </td>
                                            <td className={`py-1.5 pl-3 text-right font-medium ${newTotal > t.totalAmount ? 'text-red-600' : newTotal < t.totalAmount ? 'text-green-600' : 'text-gray-900'}`}>
                                                {formatAmount(newTotal)}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {overhead !== 0 && (
                                    <tr className="border-t border-gray-100 text-gray-500">
                                        <td className="py-1.5 pr-3 italic">{intl.formatMessage({id: 'view.analysisServerCost.overhead'})}</td>
                                        <td className="py-1.5 px-3 text-right">—</td>
                                        <td className="py-1.5 px-3 text-right">—</td>
                                        <td className="py-1.5 px-3 text-right">{formatAmount(overhead)}</td>
                                        <td className="py-1.5 px-3 text-right text-gray-400">{intl.formatMessage({id: 'view.analysisServerCost.held'})}</td>
                                        <td className="py-1.5 pl-3 text-right">{formatAmount(overhead)}</td>
                                    </tr>
                                )}
                                <tr className="border-t-2 border-gray-200 font-medium text-gray-900">
                                    <td className="py-1.5 pr-3">{intl.formatMessage({id: 'view.analysisServerCost.totalRow'})}</td>
                                    <td className="py-1.5 px-3"/>
                                    <td className="py-1.5 px-3"/>
                                    <td className="py-1.5 px-3 text-right">{formatAmount(currentTotal)}</td>
                                    <td className="py-1.5 px-3"/>
                                    <td className="py-1.5 pl-3 text-right">{formatAmount(simulatedTotal)}</td>
                                </tr>
                                {report.internalClusterCost > 0 && (
                                    <tr className="text-gray-400">
                                        <td className="py-1.5 pr-3 italic">{intl.formatMessage({id: 'view.analysisServerCost.internal'})}</td>
                                        <td className="py-1.5 px-3"/>
                                        <td className="py-1.5 px-3"/>
                                        <td className="py-1.5 px-3 text-right">{formatAmount(report.internalClusterCost)}</td>
                                        <td className="py-1.5 px-3 text-right">{intl.formatMessage({id: 'view.analysisServerCost.notCustomer'})}</td>
                                        <td className="py-1.5 pl-3 text-right">{formatAmount(report.internalClusterCost)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
};

export default AnalysisServerCostView;
